import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAlbum } from './hooks/useAlbum';
import { useUI } from './contexts/UIContext';
import { auth, signOut, db, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot, query, where, collection, limit } from './firebase';
import Login from './components/Login';
import Twemoji from './components/Twemoji';
import ProgressBar from './components/ProgressBar';
import StickerCard from './components/StickerCard';
import TeamBlock from './components/TeamBlock';
import CelebrationOverlay from './components/CelebrationOverlay';
import GridFilterBar from './components/GridFilterBar';
import ProfileView from './components/ProfileView';
import UltrawideAside from './components/UltrawideAside';
import AdBlockerBanner from './components/AdBlockerBanner';
import FirestoreErrorBanner from './components/FirestoreErrorBanner';
import OfflineBanner from './components/OfflineBanner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import StaleReservationsBanner from './components/StaleReservationsBanner';
import TradeSuggestionsBanner from './components/TradeSuggestionsBanner';
import AlbumStatsCharts from './components/AlbumStatsCharts';
import { computeTrustScores } from './utils/trustScore';
import { useNotifications } from './hooks/useNotifications';
import { ACHIEVEMENTS, computeAchievements } from './utils/achievementsUtils';
import { isAwaitingMyCompletion, isAwaitingOtherCompletion } from './utils/markProposalCompleted';
import { calculateProgress, calculateMatches, sortFriendsByPotential, calculateGlobalRarity, optimizeTrade } from './utils/tradeUtils';
import { applyGridFilter, computeGridCounts } from './utils/gridFilter';
import { fuzzyFilter } from './utils/fuzzySearch';
import { safeStorage } from './utils/storage';
import { handleFirestoreWrite } from './utils/firestoreErrors';
import './index.css';

// Heavy / route-level components — code-split to keep the initial bundle small.
// We expose the underlying import() factory so the sidebar can pre-warm a
// chunk on mouse hover before the user actually clicks (instant nav).
const importTradeMatch       = () => import('./components/TradeMatch');
const importPublicProfile    = () => import('./components/PublicProfile');
const importCommunity        = () => import('./components/Community');
const importTextMatcher      = () => import('./components/TextMatcher');
const importOnboardingModal  = () => import('./components/OnboardingModal');
const importShareImageModal  = () => import('./components/ShareImageModal');
const importAchievementsView = () => import('./components/AchievementsView');
const importProductTour      = () => import('./components/ProductTour');
const importHistoryView      = () => import('./components/HistoryView');
const importInPersonTradeView = () => import('./components/InPersonTradeView');

const TradeMatch         = lazy(importTradeMatch);
const PublicProfile      = lazy(importPublicProfile);
const Community          = lazy(importCommunity);
const TextMatcher        = lazy(importTextMatcher);
const OnboardingModal    = lazy(importOnboardingModal);
const ShareImageModal    = lazy(importShareImageModal);
const AchievementsView   = lazy(importAchievementsView);
const ProductTour        = lazy(importProductTour);
const HistoryView        = lazy(importHistoryView);
const InPersonTradeView  = lazy(importInPersonTradeView);

// Map a view id (the sidebar's activeView) to its lazy chunk import.
// Used by the sidebar to prefetch on hover.
const PREFETCH_BY_VIEW = {
  community:    importCommunity,
  achievements: importAchievementsView,
  duplicates:   importTradeMatch,
};
function prefetchView(viewId) {
  const fn = PREFETCH_BY_VIEW[viewId];
  if (fn) fn().catch(() => {});
}

function RouteFallback() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '60px 20px', color: 'var(--text-muted)', fontSize: '0.9rem',
      gap: 10
    }}>
      <div className="spinner" style={{
        width: 22, height: 22, borderRadius: '50%',
        border: '2px solid rgba(212,168,67,0.2)',
        borderTopColor: 'var(--accent-gold)',
        animation: 'spin 0.8s linear infinite'
      }} />
      <span>Cargando…</span>
    </div>
  );
}

/**
 * Wraps a StickerCard in the "Mis Repetidas" view so each card can show
 * how many copies are reserved in pending trades.
 *
 *  - When 0 reserved → looks just like a normal card
 *  - When some/all reserved → shows a 🔒 badge with the count, and the
 *    available count is also surfaced via the title attribute for screen
 *    readers.
 */
function ReservableStickerWrap({ sticker, state, rarityMap, handleToggle, setDuplicates }) {
  // Prefer the precomputed count from duplicateStickers (consistent w/
  // the subtitle math). Fall back to recomputing on the fly for safety,
  // applying the same whitespace-insensitive comparison as getReservedCount.
  const total = state.duplicates?.[sticker.number] || 0;
  let reserved = typeof sticker.reservedCount === 'number' ? sticker.reservedCount : null;
  if (reserved === null) {
    const norm = (v) => String(v ?? '').replace(/\s+/g, '').toUpperCase();
    const target = norm(sticker.number);
    reserved = 0;
    Object.values(state.trades || {}).forEach((t) => {
      if (!t || !t.reserved) return;
      const give = Array.isArray(t.give) ? t.give : [];
      if (give.some(g => norm(g) === target)) reserved++;
    });
  }
  const available = Math.max(0, total - reserved);
  const fullyReserved = reserved > 0 && available === 0;
  return (
    <div
      style={{ position: 'relative', opacity: fullyReserved ? 0.6 : 1 }}
      title={reserved > 0 ? `${total} total · ${reserved} reservada${reserved !== 1 ? 's' : ''} · ${available} disponible${available !== 1 ? 's' : ''}` : undefined}
    >
      <StickerCard
        sticker={sticker}
        collected={true}
        duplicates={total}
        onToggle={handleToggle}
        onDupChange={setDuplicates}
        rarity={rarityMap?.[sticker.number]}
      />
      {reserved > 0 && (
        <div className="reservation-badge" aria-label={`${reserved} reservada${reserved !== 1 ? 's' : ''} de ${total}`}>
          🔒 {reserved}{available > 0 ? `/${total}` : ''}
        </div>
      )}
    </div>
  );
}

function SkeletonCard({ count = 1 }) {
  return Array(count).fill(0).map((_, i) => (
    <div key={i} className="skeleton-card" style={{ marginBottom: '15px' }}>
      <div className="skeleton skeleton-circle"></div>
      <div style={{ flex: 1 }}>
        <div className="skeleton skeleton-line medium"></div>
        <div className="skeleton skeleton-line long"></div>
      </div>
    </div>
  ));
}

function FriendsView({ friends, myState, allStickers, onVisitFriend, onRemoveFriend, onAddFriend, userId, trustScores, sortedFriendsList, loadingStats }) {

  const { confirm, showNotification } = useUI();
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');

  // sortedFriendsList y loadingStats vienen de AlbumApp para compartir
  // la misma data con el banner de Sugerencias en el Resumen. Antes se
  // calculaban acá, pero eso dejaba el banner sin acceso a los matches.

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (newName.trim()) {
      const externalUid = `ext-${Date.now()}`;
      onAddFriend(externalUid, newName.trim());
      setNewName('');
      setShowModal(false);
      showNotification('Amigo externo agregado correctamente', 'success');
    }
  };

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>👥 Mis Amigos de Intercambio</h2>
          <p className="subtitle">Gestiona tus amigos para ver qué láminas pueden intercambiar</p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowModal(true)}>➕ Añadir Amigo Externo</button>
      </div>

      {showModal && (
        <div className="qr-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="qr-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3 style={{ marginBottom: '15px' }}>➕ Nuevo Amigo Externo</h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Asigna un nombre para identificar este intercambio (ej. Carlos de WhatsApp).
            </p>
            <form onSubmit={handleSubmit}>
              <input
                autoFocus
                type="text"
                className="input-field"
                style={{ marginBottom: '20px' }}
                placeholder="Nombre del amigo..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="btn" style={{ flex: 1 }} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-gold" style={{ flex: 1 }}>Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sortedFriendsList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>🤝</div>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-primary)' }}>Aún no tienes amigos agregados</p>
          <p style={{ fontSize: '0.85rem', marginTop: 8 }}>Comparte tu enlace para que otros coleccionistas te agreguen, o visita el enlace de alguien y haz clic en "Añadir Amigo".</p>
          {userId && (
            <button
              className="btn btn-gold"
              style={{ marginTop: 20, padding: '10px 24px' }}
              onClick={() => {
                const url = `${window.location.origin}/?friend=${userId}`;
                navigator.clipboard.writeText(url);
                showNotification('¡Enlace de intercambio copiado!', 'success');
              }}
            >
              🔗 Copiar mi enlace de intercambio
            </button>
          )}
        </div>
      ) : (
        <div className="stats-grid">
          {loadingStats ? (
            <SkeletonCard count={3} />
          ) : (
            sortedFriendsList.map((f) => (
              <div key={f.uid} className="stat-card friend-card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ 
                    width: 48, height: 48, borderRadius: '50%', 
                    background: f.uid.startsWith('ext-') ? 'var(--text-muted)' : 'linear-gradient(135deg, var(--accent-gold), #b8922f)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                    fontSize: '1.4rem', color: '#1a1a2e', fontWeight: 700 
                  }}>
                    {f.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {f.name}
                      {/* Trust badge: muestra cuántos tratos hemos completado
                          con este amigo. Solo aparece si > 0 — construye
                          confianza social sin agregar ruido cuando es nuevo. */}
                      {trustScores?.[f.uid] > 0 && (
                        <span
                          title={`${trustScores[f.uid]} trato${trustScores[f.uid] !== 1 ? 's' : ''} completado${trustScores[f.uid] !== 1 ? 's' : ''} con ${f.name}`}
                          style={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            background: 'rgba(74, 222, 128, 0.18)',
                            color: '#4ade80',
                            padding: '2px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(74, 222, 128, 0.35)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ✅ {trustScores[f.uid]} trato{trustScores[f.uid] !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {f.uid.startsWith('ext-') ? 'Amigo Externo' : 'Coleccionista Activo'}
                    </div>
                  </div>
                  {f.stats.match.score > 0 && (
                    <div className="match-badge">🔥 {f.stats.match.score}</div>
                  )}
                </div>

                {!f.uid.startsWith('ext-') && (
                  <div className="friend-stats-area">
                    <div className="friend-stat-row">
                      <span>Progreso:</span>
                      <strong>{f.stats.progress.pct}%</strong>
                    </div>
                    <ProgressBar pct={f.stats.progress.pct} width="100%" />
                    
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <div className="mini-stat-box" style={{ background: 'rgba(74, 222, 128, 0.1)', color: '#4ade80' }}>
                        <span className="label">Me sirve</span>
                        <span className="val">{f.stats.match.receive.length}</span>
                      </div>
                      <div className="mini-stat-box" style={{ background: 'rgba(96, 165, 250, 0.1)', color: '#60a5fa' }}>
                        <span className="label">Le sirve</span>
                        <span className="val">{f.stats.match.give.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                {f.uid.startsWith('ext-') && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--border-color)', borderRadius: '8px', padding: '20px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                    Sin datos de inventario.<br/>Usa "Ver Álbum" para gestionar manualmente.
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                  <button className="btn btn-gold" style={{ flex: 1 }} onClick={() => onVisitFriend(f.uid)}>Ver Álbum 🤝</button>
                  <button className="btn btn-danger" onClick={async () => { 
                    if(await confirm(`¿Quitar a ${f.name} de tus amigos?`)) onRemoveFriend(f.uid); 
                  }}>🗑️</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ProposalsView({ incoming, outgoing, onVisitFriend, onRemove, onSync, myUid, trustScores }) {
    const [tab, setTab] = useState('incoming');
    const [syncing, setSyncing] = useState(null);
    const list = tab === 'incoming' ? incoming : outgoing;

    return (
      <div>
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h2>✨ Propuestas de Intercambio</h2>
            <p className="subtitle">Gestiona tus solicitudes de intercambio</p>
          </div>
          <div style={{ display: 'flex', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '12px' }}>
            <button 
              className={`btn ${tab === 'incoming' ? 'btn-gold' : ''}`} 
              style={{ border: 'none', background: tab === 'incoming' ? 'var(--accent-gold)' : 'transparent' }}
              onClick={() => setTab('incoming')}
            >
              Recibidas ({incoming.length})
            </button>
            <button 
              className={`btn ${tab === 'outgoing' ? 'btn-gold' : ''}`} 
              style={{ border: 'none', background: tab === 'outgoing' ? 'var(--accent-gold)' : 'transparent' }}
              onClick={() => setTab('outgoing')}
            >
              Enviadas ({outgoing.length})
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>{tab === 'incoming' ? '📬' : '📤'}</div>
            <p style={{ fontSize: '1.1rem' }}>No tienes propuestas {tab === 'incoming' ? 'pendientes' : 'enviadas'}</p>
          </div>
        ) : (
          <div className="stats-grid">
            {list.map(p => (
              <div key={p.id} className="stat-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: tab === 'incoming' ? 'var(--accent-gold)' : 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#1a1a2e', fontWeight: 700 }}>
                    {(tab === 'incoming' ? p.fromName : (p.toName || 'C')).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{tab === 'incoming' ? p.fromName : (p.toName || 'Coleccionista')}</span>
                      {(() => {
                        const counterpart = tab === 'incoming' ? p.fromUid : p.toUid;
                        const score = trustScores?.[counterpart] || 0;
                        if (score <= 0) return null;
                        return (
                          <span
                            title={`Has completado ${score} trato${score !== 1 ? 's' : ''} con esta persona`}
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              background: 'rgba(74, 222, 128, 0.18)',
                              color: '#4ade80',
                              padding: '1px 7px',
                              borderRadius: 999,
                              border: '1px solid rgba(74, 222, 128, 0.35)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ✅ {score}
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: 8 }}>
                      <span>{tab === 'incoming' ? 'Quiere negociar contigo' : 'Propuesta enviada'}</span>
                      {p.createdAt && (
                        <span title={new Date(p.createdAt?.seconds ? p.createdAt.seconds * 1000 : p.createdAt).toLocaleString()}>
                          · {(() => {
                            const ms = p.createdAt?.seconds ? p.createdAt.seconds * 1000 : p.createdAt;
                            const mins = Math.floor((Date.now() - ms) / 60000);
                            if (mins < 1)   return 'ahora';
                            if (mins < 60)  return `hace ${mins}m`;
                            const hrs = Math.floor(mins / 60);
                            if (hrs < 24)   return `hace ${hrs}h`;
                            return `hace ${Math.floor(hrs / 24)}d`;
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  {/* Banner del modelo bilateral: estado distinto si el
                      otro lado ya confirmó (urgente) vs si yo ya confirmé
                      y espero al otro lado (informativo). */}
                  {isAwaitingMyCompletion(p, myUid) && (
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(74, 222, 128, 0.12)',
                      borderLeft: '4px solid #4ade80',
                      borderRadius: 6,
                      fontSize: '0.82rem',
                      color: '#4ade80',
                      marginBottom: 12,
                      lineHeight: 1.4,
                      fontWeight: 600,
                    }}>
                      ✅ {tab === 'incoming' ? p.fromName : (p.toName || 'El otro coleccionista')} confirmó el intercambio.
                      Da click en <strong>Ver Propuesta</strong> y confirma tu lado para actualizar tu álbum.
                    </div>
                  )}
                  {isAwaitingOtherCompletion(p, myUid) && (
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(96, 165, 250, 0.10)',
                      borderLeft: '4px solid #60a5fa',
                      borderRadius: 6,
                      fontSize: '0.82rem',
                      color: '#60a5fa',
                      marginBottom: 12,
                      lineHeight: 1.4,
                    }}>
                      ⏳ Ya confirmaste tu lado. Esperando a que {tab === 'incoming' ? p.fromName : (p.toName || 'el otro coleccionista')} también confirme para cerrar el trato.
                    </div>
                  )}
                  {p.modifiedBySystem && !isAwaitingMyCompletion(p, myUid) && !isAwaitingOtherCompletion(p, myUid) && (
                    <div style={{ padding: '8px 12px', background: 'rgba(212, 168, 67, 0.1)', borderLeft: '3px solid var(--accent-gold)', borderRadius: '4px', fontSize: '0.75rem', color: 'var(--accent-gold)', marginBottom: '12px', lineHeight: '1.4' }}>
                      <div>{p.systemMessage}</div>
                      {/* Chips de las láminas que cayeron del trato. La
                          UI antes solo mostraba el mensaje genérico; ahora
                          las separamos por dirección para que el usuario
                          vea EXACTAMENTE qué se modificó.
                          - removedReceive: lo que el completer ya no
                            necesita recibir (= salió de SU lado receive).
                          - removedGive:    lo que el completer ya no
                            puede entregar (= salió de SU lado give). */}
                      {(p.removedReceive?.length > 0 || p.removedGive?.length > 0) && (
                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {p.removedReceive?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ya no se reciben:</span>
                              {p.removedReceive.map(n => (
                                <span key={`rr-${n}`} style={{
                                  fontSize: '0.65rem',
                                  background: 'rgba(239, 68, 68, 0.15)',
                                  color: '#fca5a5',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  textDecoration: 'line-through',
                                  textDecorationColor: 'rgba(239, 68, 68, 0.5)',
                                }}>{n}</span>
                              ))}
                            </div>
                          )}
                          {p.removedGive?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ya no se entregan:</span>
                              {p.removedGive.map(n => (
                                <span key={`rg-${n}`} style={{
                                  fontSize: '0.65rem',
                                  background: 'rgba(239, 68, 68, 0.15)',
                                  color: '#fca5a5',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  textDecoration: 'line-through',
                                  textDecorationColor: 'rgba(239, 68, 68, 0.5)',
                                }}>{n}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#60a5fa', marginBottom: 6, fontWeight: 600 }}>
                    {tab === 'incoming' ? 'Tú recibes' : 'Ellos reciben'}: {p.give?.length || 0}
                    {p.originalGive && p.originalGive.length !== p.give?.length && (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', marginLeft: 8 }}>(Originalmente: {p.originalGive.length})</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                    {p.give?.map(n => <span key={n} style={{ fontSize: '0.65rem', background: '#1e3a8a', color: '#60a5fa', padding: '2px 6px', borderRadius: '4px' }}>{n}</span>)}
                  </div>
                  
                  <div style={{ fontSize: '0.75rem', color: '#4ade80', marginBottom: 6, fontWeight: 600 }}>
                    {tab === 'incoming' ? 'Tú entregas' : 'Ellos entregan'}: {p.receive?.length || 0}
                    {p.originalReceive && p.originalReceive.length !== p.receive?.length && (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', marginLeft: 8 }}>(Originalmente: {p.originalReceive.length})</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {p.receive?.map(n => <span key={n} style={{ fontSize: '0.65rem', background: '#1a4731', color: '#4ade80', padding: '2px 6px', borderRadius: '4px' }}>{n}</span>)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-gold" style={{ flex: 1 }} onClick={() => onVisitFriend(tab === 'incoming' ? p.fromUid : p.toUid)}>
                    {tab === 'incoming' ? 'Ver Match Completo 🤝' : 'Ver Mi Propuesta 🔍'}
                  </button>
                  <button className="btn" onClick={async () => {
                    setSyncing(p.id);
                    await onSync(p);
                    setSyncing(null);
                  }} title="Sincronizar con inventarios actuales" disabled={syncing === p.id}>
                    {syncing === p.id ? '⌛' : '🔄'}
                  </button>
                  <button className="btn" onClick={() => onRemove(p.id, tab === 'incoming' ? p.fromUid : p.toUid)} title={tab === 'incoming' ? 'Rechazar' : 'Cancelar'}>
                    {tab === 'incoming' ? '🗑️' : '🚫'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
}

const normalizeStickerNumber = (val) => {
  if (!val) return '';
  let clean = val.trim().replace(/\s+/g, ' ').toUpperCase();
  if (clean === '00') return '00';
  
  // If it's already in the right format (CODE NUMBER), return it
  if (/^[A-Z]+\s\d+$/.test(clean)) return clean;
  
  // If it's CODE-NUMBER or CODENUMBER, fix it
  const match = clean.replace(/[-\s]/g, '').match(/^([A-Z]+)(\d+)$/);
  if (match) {
    return `${match[1]} ${match[2]}`;
  }
  return clean;
};

function RarityView({ allStickers }) {
  const [rarityData, setRarityData] = useState({ hardest: [], mostCommon: [], totalUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function calculateRarity() {
      try {
        // Sample of ~300 users instead of the entire collection.
        // PublicProfile already samples (150) for the same calculation;
        // before this fix RarityView was unbounded and burned through the
        // free-tier 50k reads/day with one popular session.
        const usersSnap = await getDocs(query(collection(db, 'users'), limit(300)));
        if (cancelled) return;
        const counts = {};
        let totalUsers = 0;

        usersSnap.forEach(doc => {
          totalUsers++;
          const collected = doc.data().collected || {};
          Object.keys(collected).forEach(num => {
            counts[num] = (counts[num] || 0) + 1;
          });
        });

        const rarityArray = allStickers.map(s => ({
          ...s,
          count: counts[s.number] || 0,
          rarity: totalUsers > 0 ? ((counts[s.number] || 0) / totalUsers * 100).toFixed(1) : 0
        }));

        const sorted = [...rarityArray].sort((a, b) => a.count - b.count);
        const hardest = sorted.slice(0, 10);
        // Reverse the sort and skip zero-count stickers — otherwise on
        // a brand-new project "most common" would show ones nobody has.
        const mostCommon = [...rarityArray]
          .filter(s => s.count > 0)
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        if (!cancelled) {
          setRarityData({ hardest, mostCommon, totalUsers });
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('RarityView fetch failed:', err);
        setError(err);
        setLoading(false);
      }
    }
    calculateRarity();
    return () => { cancelled = true; };
  }, [allStickers]);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}>Analizando el mercado global... 💎</div>;

  if (error) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚠️</div>
      <p>No pudimos cargar la rareza global ahora mismo.</p>
      <p style={{ fontSize: '0.85rem' }}>Inténtalo de nuevo en unos minutos.</p>
    </div>
  );

  return (
    <div>
      <div className="section-header">
        <h2>💎 Rareza Global</h2>
        <p className="subtitle">Basado en {rarityData.totalUsers} álbumes activos en la plataforma</p>
      </div>
      
      <div className="group-stats-grid">
        <div className="stat-card">
          <h3 style={{ color: '#ef4444', marginBottom: 15 }}>🔥 Las 10 Más Difíciles</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rarityData.hardest.map(s => (
              <div key={s.number} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                <span><strong>{s.number}</strong> {s.label}</span>
                <span style={{ color: '#ef4444' }}>{s.rarity}% la tienen</span>
              </div>
            ))}
          </div>
        </div>
        <div className="stat-card">
          <h3 style={{ color: '#4ade80', marginBottom: 15 }}>✅ Las 10 Más Comunes</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rarityData.mostCommon.map(s => (
              <div key={s.number} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
                <span><strong>{s.number}</strong> {s.label}</span>
                <span style={{ color: '#4ade80' }}>{s.rarity}% la tienen</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReconcileView({ appDuplicates, onSync, allStickers }) {
  const [sessionDuplicates, setSessionDuplicates] = useState({});
  const [quickInput, setQuickInput] = useState('');
  const [bulkInput, setBulkInput] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const { confirm, showNotification } = useUI();

  const handleQuickAdd = (e) => {
    e.preventDefault();
    const val = normalizeStickerNumber(quickInput);
    if (!val) return;
    
    const sticker = allStickers.find(s => s.number === val);
    if (!sticker) {
      showNotification(`Lámina "${val}" no encontrada en el álbum.`, 'error');
      setQuickInput('');
      return;
    }

    setSessionDuplicates(prev => ({
      ...prev,
      [val]: (prev[val] || 0) + 1
    }));
    setQuickInput('');
  };

  const handleBulkProcess = () => {
    const rawItems = bulkInput.split(/[\n,]+/);
    const nums = rawItems
      .map(n => {
        const base = n.trim().split(' (')[0];
        return normalizeStickerNumber(base);
      })
      .filter(n => n);
    
    if (nums.length === 0) return;

    const newSession = { ...sessionDuplicates };
    nums.forEach(num => {
      newSession[num] = (newSession[num] || 0) + 1;
    });
    setSessionDuplicates(newSession);
    setBulkInput('');
    setShowBulk(false);
    showNotification(`${nums.length} láminas añadidas al conteo físico`, 'success');
  };

  const discrepancies = useMemo(() => {
    const allKeys = new Set([...Object.keys(appDuplicates), ...Object.keys(sessionDuplicates)]);
    const list = [];
    allKeys.forEach(num => {
      const appCount = appDuplicates[num] || 0;
      const handCount = sessionDuplicates[num] || 0;
      if (appCount !== handCount) {
        list.push({ num, appCount, handCount, diff: handCount - appCount });
      }
    });
    // Sort by number
    list.sort((a, b) => a.num.localeCompare(b.num, undefined, { numeric: true }));
    return list;
  }, [appDuplicates, sessionDuplicates]);

  const handleSync = async () => {
    const totalHand = Object.values(sessionDuplicates).reduce((a, b) => a + b, 0);
    if (await confirm(`¿Estás seguro? La App se actualizará para tener EXACTAMENTE las ${totalHand} repetidas que has contado físicamente.`)) {
      onSync(sessionDuplicates);
      setSessionDuplicates({});
    }
  };

  return (
    <div>
      <div className="section-header">
        <h2>🔍 Auditoría de Inventario</h2>
        <p className="subtitle">Compara lo que tienes físicamente contra la App y corrige descuadres</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="stat-card" style={{ padding: '25px' }}>
          <h3 style={{ marginBottom: '15px', fontSize: '1rem' }}>📥 1. Ingresar láminas físicas</h3>
          <form onSubmit={handleQuickAdd} style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <input 
              type="text" 
              className="btn" 
              style={{ flex: 1, textAlign: 'left', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', padding: '0 15px' }}
              placeholder="Escribe número y pulsa Enter (ej: MEX 1)..."
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
            />
            <button type="submit" className="btn btn-gold">Añadir</button>
          </form>
          <button className="btn" style={{ width: '100%' }} onClick={() => setShowBulk(!showBulk)}>
            {showBulk ? 'Cerrar Carga Masiva' : '📂 Cargar Lista Masiva'}
          </button>
          
          {showBulk && (
            <div style={{ marginTop: '15px', animation: 'slideDown 0.3s ease-out' }}>
              <textarea 
                style={{ width: '100%', height: '120px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px', color: 'white', marginBottom: '10px' }}
                placeholder="Pega aquí tu lista separada por comas..."
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
              />
              <button className="btn btn-gold" style={{ width: '100%' }} onClick={handleBulkProcess}>Procesar Lista</button>
            </div>
          )}

          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Láminas contadas en esta sesión:</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-gold)' }}>
              {Object.values(sessionDuplicates).reduce((a, b) => a + b, 0)}
            </div>
            <button className="btn btn-danger" style={{ marginTop: '10px', fontSize: '0.75rem', padding: '5px 10px' }} onClick={() => setSessionDuplicates({})}>Borrar conteo actual</button>
          </div>
        </div>

        <div className="stat-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginBottom: '15px', fontSize: '1rem' }}>⚖️ 2. Resultado de la comparación</h3>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '300px', marginBottom: '20px' }}>
            {discrepancies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#4ade80' }}>
                <div style={{ fontSize: '2rem' }}>✅</div>
                <p>¡Todo cuadra!</p>
                <p style={{ fontSize: '0.8rem', opacity: 0.7 }}>Tu conteo físico coincide con la App.</p>
              </div>
            ) : (
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '8px 0' }}>Lámina</th>
                    <th>Físico</th>
                    <th>App</th>
                    <th>Dif.</th>
                  </tr>
                </thead>
                <tbody>
                  {discrepancies.map(d => (
                    <tr key={d.num} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding: '8px 0' }}><strong>{d.num}</strong></td>
                      <td>{d.handCount}</td>
                      <td>{d.appCount}</td>
                      <td style={{ color: d.diff > 0 ? '#4ade80' : '#ef4444', fontWeight: 700 }}>
                        {d.diff > 0 ? `+${d.diff}` : d.diff}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <button 
            className="btn btn-gold" 
            style={{ width: '100%', height: '50px', fontSize: '1rem' }} 
            onClick={handleSync}
            disabled={Object.keys(sessionDuplicates).length === 0}
          >
            🔄 Sincronizar Inventario
          </button>
        </div>
      </div>
    </div>
  );
}

function AlbumApp({ user, friendUid, onClearFriend }) {
  const {
    state, allStickers, albumData, totalProgress, totalDuplicates,
    dataLoading, toggleSticker, markStickers, setDuplicates, exportData, importData,
    resetAll, updateProfile, addFriend, removeFriend,
    setTrade, removeTrade, clearAllReservations, getReservedCount, getAvailableCount, completeTrade, rectifyInventory, syncAllDuplicates,
    exportAllMyData, deleteMyAccount,
  } = useAlbum(user);

  // ── Debug helpers: lets the user inspect reservations from DevTools ──
  // window.__debugReservations() prints a table of all trades, whether
  // they're flagged reserved, and what stickers are in their give list.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__state = state;
    window.__debugReservations = () => {
      const rows = Object.entries(state.trades || {}).map(([fid, t]) => ({
        friend: state.friends?.[fid]?.name || fid,
        reserved: t?.reserved === true ? '✅ SÍ' : '❌ no',
        give: Array.isArray(t?.give) ? t.give.join(', ') : '(vacío)',
        receive: Array.isArray(t?.receive) ? t.receive.length : 0,
      }));
      if (rows.length === 0) {
        console.log('No tienes ningún trade guardado en state.trades.');
      } else {
        console.table(rows);
        const totalReserved = rows.filter(r => r.reserved.includes('SÍ')).reduce((sum, r) => sum + r.give.split(',').filter(Boolean).length, 0);
        console.log(`Total láminas reservadas: ${totalReserved}`);
      }
      return rows;
    };
  }, [state]);

  // ── All state declarations (must precede any effects that reference them) ──
  const [celebration, setCelebration] = useState(null);
  const [rarityMap, setRarityMap] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showShareImage, setShowShareImage] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);
  // (prevStateRef / prevRarityRef removed — achievement detection now
  // uses a persisted localStorage set, see the unlock useEffect below.)
  const [activeView, setActiveView] = useState('stats');
  const [previousView, setPreviousView] = useState('stats');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState({ name: '', photo: '', city: '', phone: '' });
  // El perfil se hidrata vía onSnapshot. Hasta que la primera lectura
  // resuelve, `profile.name === ''` aunque el usuario sí tenga nombre
  // guardado en Firestore. Sin este flag, el onboarding se disparaba
  // de manera espuria en cada reload (o tras cualquier error que
  // desmonte/remonte AlbumApp).
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [incomingProposals, setIncomingProposals] = useState([]);
  const [outgoingProposals, setOutgoingProposals] = useState([]);
  const prevProposalsCount = useRef(null);
  // Snapshot of which proposal IDs were pending on the LAST fire of
  // each direction's listener. Used to detect when a proposal
  // disappears so we can auto-release the matching local reservation.
  // Keys are proposal IDs, values are the friend UID we'd need to
  // unlock on disappearance.
  const prevIncomingIdsRef = useRef(null);
  const prevOutgoingIdsRef = useRef(null);
  // Refs over state.trades / setTrade so the proposal-disappearance
  // effect can read the latest values without subscribing to every
  // sticker toggle (which would re-run the effect needlessly).
  const stateTradesRef = useRef({});
  const setTradeRef = useRef(null);
  const [search, setSearch] = useState('');
  const [dupSearch, setDupSearch] = useState('');
  const [dupSort, setDupSort] = useState('country');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [highlightedSticker, setHighlightedSticker] = useState(null);
  const [gridFilter, setGridFilter] = useState('all'); // 'all' | 'missing' | 'duplicates' | 'collected'
  const [gridLayout, setGridLayout] = useState(() => safeStorage.get('gridLayout') || 'grid'); // 'grid' | 'list'
  const [showTour, setShowTour] = useState(false);
  const [quickAdd, setQuickAdd] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [showInPersonTrade, setShowInPersonTrade] = useState(false);
  const [showTextMatcher, setShowTextMatcher] = useState(false);
  const [notifBannerDismissed, setNotifBannerDismissed] = useState(
    () => !!safeStorage.get('notifBannerDismissed')
  );
  const { showNotification, confirm } = useUI();
  const { isSupported: notifSupported, currentPermission, requestPermission, notifyNewProposal } = useNotifications(user.uid);
  const fileRef = useRef(null);

  // Cerrar el menú overflow al hacer clic fuera
  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e) => { if (overflowRef.current && !overflowRef.current.contains(e.target)) setOverflowOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  useEffect(() => {
    if (allStickers.length === 0) return;

    // 1) Hot path: serve the cached rarityMap if it's < 6h old. PublicProfile
    //    already does this; mirroring the strategy here saves 300+ reads per
    //    session for returning users and keeps the rarityMap available
    //    immediately instead of after a network round-trip.
    const RARITY_CACHE_KEY = 'rarity_map_v1';
    const RARITY_TTL_MS = 6 * 60 * 60 * 1000;
    const cached = safeStorage.getJSON(RARITY_CACHE_KEY);
    if (cached && cached.ts && (Date.now() - cached.ts) < RARITY_TTL_MS && cached.map) {
      setRarityMap(cached.map);
      return; // skip the Firestore fetch entirely
    }

    // 2) Cache miss → fetch a sample (not the whole collection) and cache.
    let cancelled = false;
    async function loadRarity() {
      try {
        const snap = await getDocs(query(collection(db, 'users'), limit(300)));
        if (cancelled) return;
        const allUsersData = snap.docs.map(d => d.data());
        const map = calculateGlobalRarity(allUsersData, allStickers);
        setRarityMap(map);
        safeStorage.setJSON(RARITY_CACHE_KEY, { ts: Date.now(), map });
      } catch (e) {
        console.error("Error calculating global rarity", e);
        // Non-fatal — achievements that depend on rarity just won't fire
        // until the next session manages to load it.
      }
    }
    loadRarity();
    return () => { cancelled = true; };
  }, [allStickers]);

  // Mostrar onboarding solo a usuarios NUEVOS: ni el state del álbum
  // ni el perfil pueden estar aún cargando (si no, `profile.name`
  // arranca '' incluso para usuarios viejos y disparábamos el modal
  // de onboarding en cada reload).
  useEffect(() => {
    if (dataLoading || !profileLoaded) return;
    const key = `onboardingDone_${user.uid}`;
    if (safeStorage.get(key)) return;
    if (!profile.name) setShowOnboarding(true);
  }, [dataLoading, profileLoaded, profile.name, user.uid]);

  // Keep refs in sync so the proposal-disappearance detector below can
  // read CURRENT state.trades + setTrade without making them effect
  // deps (which would re-run the detector on every sticker toggle).
  useEffect(() => { stateTradesRef.current = state.trades || {}; }, [state.trades]);
  useEffect(() => { setTradeRef.current = setTrade; }, [setTrade]);

  // Auto-release reservations when the corresponding proposal goes
  // away. Two ways a proposal can disappear from a user's pending
  // list:
  //   1. The other party cancelled / rejected it (deleteDoc)
  //   2. Either party completed it (status flips from 'pending' to
  //      'completed' or 'cancelled')
  //
  // In case (2) the completer's local trade is already gone, so the
  // `state.trades[otherUid]?.reserved` guard below skips the work.
  // Case (1) is what the user explicitly asked about: friend had
  // reserved their give-side, sender cancels, friend's lock stays
  // forever. Now it's freed automatically.
  useEffect(() => {
    if (dataLoading) return;

    const checkSide = (currentList, prevIdsRef, directionLabel) => {
      const currentMap = new Map();
      currentList.forEach(p => {
        const otherUid = p.fromUid === user.uid ? p.toUid : p.fromUid;
        const otherName = (p.fromUid === user.uid ? p.toName : p.fromName) || 'tu amigo';
        currentMap.set(p.id, { otherUid, otherName });
      });

      const prev = prevIdsRef.current;
      prevIdsRef.current = currentMap;
      // First fire on mount: just seed, don't act. Otherwise we'd
      // think every proposal "disappeared" on initial render.
      if (prev === null) return;

      prev.forEach((info, id) => {
        if (currentMap.has(id)) return; // still there
        const localTrade = stateTradesRef.current?.[info.otherUid];
        if (localTrade?.reserved) {
          // Release: keep the trade record (so the conversation /
          // history with that friend isn't wiped), just flip reserved.
          setTradeRef.current?.(info.otherUid, {
            receive: localTrade.receive || [],
            give: localTrade.give || [],
            reserved: false,
            note: localTrade.note || '',
          }, { immediate: true });
          showNotification(
            `🔓 Tu trato ${directionLabel} ${info.otherName} ya no está pendiente. Las láminas reservadas están de nuevo disponibles.`,
            'info'
          );
        }
      });
    };

    checkSide(incomingProposals, prevIncomingIdsRef, 'con');
    checkSide(outgoingProposals, prevOutgoingIdsRef, 'con');
  }, [incomingProposals, outgoingProposals, dataLoading, user.uid, showNotification]);

  // Hidratación: para cada propuesta enviada pendiente, garantizar que
  // exista un trade local con reserved=true. Sin esto, las propuestas
  // ENVIADAS antes de que handleSendProposal auto-reservara (commits
  // recientes) seguían sin filtrar en el perfil público — el usuario
  // veía como faltantes las láminas que ya esperaba recibir vía esa
  // propuesta.
  //
  // El efecto solo agrega entradas FALTANTES — nunca pisa un trade
  // ya existente del usuario (puede tener selecciones distintas a la
  // propuesta original si el usuario está negociando contra-ofertas).
  const hydratedProposalsRef = useRef(new Set());
  useEffect(() => {
    if (dataLoading || !setTrade) return;
    outgoingProposals.forEach((p) => {
      if (hydratedProposalsRef.current.has(p.id)) return;
      hydratedProposalsRef.current.add(p.id);
      const toUid = p.toUid;
      const existing = state.trades?.[toUid];
      // Si ya hay trade reservado, no tocar. Si no hay trade o está
      // sin reservar, hidratar con la propuesta.
      if (existing?.reserved) return;
      setTrade(toUid, {
        receive: existing?.receive?.length ? existing.receive : (p.receive || []),
        give: existing?.give?.length ? existing.give : (p.give || []),
        reserved: true,
        note: existing?.note || '',
      }, { immediate: true });
    });
  }, [outgoingProposals, dataLoading, state.trades, setTrade]);

  // Detectar nuevos logros cuando cambia el estado del álbum.
  //
  // Antes este efecto comparaba prev vs current state. Problema: en
  // cada F5, prevRarityRef arranca {} y cuando carga rarityMap async,
  // el comparador interpretaba "ahora hay legendarias que no había"
  // y reventaba toasts de logros que el usuario ya tenía hace meses.
  //
  // Ahora persistimos los IDs desbloqueados en localStorage. Solo
  // disparamos toast cuando un logro entra en done=true y NO estaba
  // ya en el set persistido. Eso sobrevive recargas y carga tardía
  // de rarityMap sin re-notificar.
  useEffect(() => {
    if (dataLoading || !state) return;
    const seenKey = `unlockedAchievements_${user.uid}`;
    const seen = new Set(safeStorage.getJSON(seenKey) || []);

    const current = computeAchievements(state, { rarityMap });
    const newlyUnlocked = current.filter(c => c.done && !seen.has(c.achievement.id));

    // First time this user lands on this device: silently mark all
    // currently-done achievements as seen, no toast spam.
    const isFirstRun = !safeStorage.get(seenKey);

    let changed = false;
    current.forEach(c => {
      if (c.done && !seen.has(c.achievement.id)) {
        seen.add(c.achievement.id);
        changed = true;
      }
    });
    if (changed) safeStorage.setJSON(seenKey, Array.from(seen));

    if (!isFirstRun) {
      newlyUnlocked.forEach(c => {
        showNotification(`${c.achievement.icon} ¡Logro desbloqueado! "${c.achievement.title}"`, 'achievement');
      });
    }
  }, [state, dataLoading, rarityMap, user.uid, showNotification]);

  const handleToggle = (number) => {
    const isCollected = !!state.collected[number];
    const sticker = allStickers.find(s => s.number === number);
    
    toggleSticker(number);

    if (!isCollected && sticker) {
      // 1. Check Team
      if (sticker.team) {
        const team = albumData.groups.flatMap(g => g.teams).find(t => t.id === sticker.team);
        if (team) {
          const teamStickers = team.stickers;
          const collectedCount = teamStickers.filter(s => !!state.collected[s.number] || s.number === number).length;
          if (collectedCount === teamStickers.length) {
            setCelebration(`¡Equipo ${team.name} Completado! 🏆`);
          }
        }
      }
      
      // 2. Check Section
      if (sticker.section) {
        const section = albumData.sections.find(s => s.id === sticker.section);
        if (section) {
          const sectionStickers = section.stickers;
          const collectedCount = sectionStickers.filter(s => !!state.collected[s.number] || s.number === number).length;
          if (collectedCount === sectionStickers.length) {
            setCelebration(`¡Sección ${section.name} Completada! ✨`);
          }
        }
      }
    }
  };

  const getProgress = (stickers) => {
    const have = stickers.filter(s => !!state.collected[s.number]).length;
    const total = stickers.length;
    const pct = total > 0 ? Math.round((have / total) * 100) : 0;
    return { have, total, pct };
  };

  const navItems = useMemo(() => {
    const items = [
      { id: 'stats', label: 'Resumen', icon: '📊' },
      { id: 'profile', label: 'Mi Perfil', icon: '👤' },
      { id: 'community', label: 'Mercado Global', icon: '🌍' },
      { id: 'proposals', label: 'Propuestas', icon: '✨', badge: incomingProposals.length > 0 ? incomingProposals.length : null },
      { id: 'achievements', label: 'Logros', icon: '🏅' },
      { id: 'history', label: 'Mi Historial', icon: '🕒' },
      { id: 'rarity', label: 'Rareza Global', icon: '💎' },
      { id: 'reconcile', label: 'Auditoría', icon: '🔍' },
      { id: 'friends', label: 'Amigos', icon: '👥' },
      { id: 'duplicates', label: 'Repetidas', icon: '🔄' },
      ...albumData.sections.map(s => ({ id: `section-${s.id}`, label: s.name, icon: s.icon })),
      ...albumData.groups.map(g => ({
        id: `group-${g.id}`, label: g.name, icon: '⚽',
        teams: g.teams.map(t => ({ id: `team-${t.code}`, label: t.name, flag: t.flag, code: t.code }))
      }))
    ];
    return items;
  }, [albumData, incomingProposals.length]);

  // Set de láminas que el usuario espera recibir de tratos RESERVADOS
  // (no de borradores). Lo usamos para distinguirlas visualmente de
  // las simplemente "faltantes" en la cuadrícula del álbum y para
  // excluirlas de los conteos de "faltantes" donde tenga sentido.
  // El normalizado quita espacios para tolerar "FWC 12" vs "FWC12".
  // Trust score: map { friendUid: tradesCompletados }. Lo usamos para
  // mostrar un badge "✅ N tratos" al lado del nombre del amigo en
  // FriendsView y ProposalsView — construye confianza social.
  const trustScores = useMemo(() => computeTrustScores(state.history), [state.history]);

  // ── Friend stats (lifted from FriendsView) ─────────────────────────
  // Antes vivía dentro de FriendsView, pero el banner de Sugerencias
  // Proactivas en el Resumen necesita esta misma data (sortedFriendsList).
  // Subimos el estado a AlbumApp para que ambas vistas lo compartan sin
  // duplicar fetches.
  const [friendsStats, setFriendsStats] = useState({});
  const [loadingFriendsStats, setLoadingFriendsStats] = useState(false);
  const friendInventoriesRef = useRef({}); // uid → { collected, duplicates }
  const [statsRecomputeKey, setStatsRecomputeKey] = useState(0);

  const friendUidsKey = useMemo(() => {
    return Object.keys(state.friends || {}).filter(u => !u.startsWith('ext-')).sort().join('|');
  }, [state.friends]);

  useEffect(() => {
    let cancelled = false;
    async function loadInventories() {
      setLoadingFriendsStats(true);
      const uids = friendUidsKey.split('|').filter(Boolean);
      await Promise.all(uids.map(async (uid) => {
        if (friendInventoriesRef.current[uid]) return;
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) friendInventoriesRef.current[uid] = snap.data();
        } catch (e) {
          console.error('Error loading friend inventory', uid, e);
        }
      }));
      if (cancelled) return;
      setLoadingFriendsStats(false);
      setStatsRecomputeKey(k => k + 1);
    }
    if (friendUidsKey) loadInventories();
    return () => { cancelled = true; };
  }, [friendUidsKey]);

  useEffect(() => {
    const stats = {};
    Object.entries(friendInventoriesRef.current).forEach(([uid, data]) => {
      const progress = calculateProgress(allStickers, data.collected || {});
      const match = calculateMatches(
        state.collected || {},
        state.duplicates || {},
        data.collected || {},
        data.duplicates || {}
      );
      stats[uid] = { progress, match };
    });
    setFriendsStats(stats);
  }, [statsRecomputeKey, state.collected, state.duplicates, allStickers]);

  const sortedFriendsList = useMemo(() => {
    const entries = Object.entries(state.friends || {});
    const listWithStats = entries.map(([uid, f]) => ({
      uid,
      ...f,
      stats: friendsStats[uid] || { progress: { pct: 0, have: 0, total: allStickers.length }, match: { give: [], receive: [], score: 0 } }
    }));
    if (Object.keys(friendsStats).length > 0) {
      return listWithStats.sort((a, b) => {
        if (b.stats.match.score !== a.stats.match.score) return b.stats.match.score - a.stats.match.score;
        return b.stats.progress.pct - a.stats.progress.pct;
      });
    }
    return listWithStats;
  }, [state.friends, friendsStats, allStickers]);

  const expectedIncoming = useMemo(() => {
    const norm = (v) => String(v ?? '').replace(/\s+/g, '').toUpperCase();
    const set = new Set();
    Object.values(state.trades || {}).forEach(t => {
      if (!t || !t.reserved) return;
      (t.receive || []).forEach(n => set.add(norm(n)));
    });
    return set;
  }, [state.trades]);

  const isIncoming = useCallback(
    (number) => {
      if (state.collected?.[number]) return false; // ya la tienes
      const norm = String(number ?? '').replace(/\s+/g, '').toUpperCase();
      return expectedIncoming.has(norm);
    },
    [expectedIncoming, state.collected],
  );

  const duplicateStickers = useMemo(() => {
    // Whitespace-insensitive lookup. Defends against duplicates that
    // ended up stored under a normalized key (e.g. "FWC12") while the
    // canonical sticker number has a space ("FWC 12"). Without this,
    // affected láminas — especially FWC ones — would silently
    // disappear from "Mis Repetidas".
    const dupNormMap = {};
    Object.entries(state.duplicates || {}).forEach(([k, v]) => {
      const nk = String(k).replace(/\s+/g, '').toUpperCase();
      // Prefer the exact-match value if the same sticker exists under
      // both forms — otherwise sum them so the count survives a fix.
      dupNormMap[nk] = (dupNormMap[nk] || 0) + (Number(v) || 0);
    });
    const lookupDupes = (number) => {
      const exact = state.duplicates?.[number];
      if (exact) return exact;
      const norm = String(number).replace(/\s+/g, '').toUpperCase();
      return dupNormMap[norm] || 0;
    };

    let filtered = allStickers.filter(s => lookupDupes(s.number) > 0)
      .map(s => {
        const dupes = lookupDupes(s.number);
        const reservedCount = getReservedCount(s.number);
        return {
          ...s,
          dupes,
          reservedCount,
          availableCount: Math.max(0, dupes - reservedCount),
        };
      });

    if (dupSearch) {
      const q = dupSearch.toLowerCase().replace(/\s+/g, '');
      filtered = filtered.filter(s => {
        const normalizedLabel = s.label.toLowerCase().replace(/\s+/g, '');
        const normalizedNumber = String(s.number).toLowerCase().replace(/\s+/g, '');
        return normalizedLabel.includes(q) || normalizedNumber.includes(q);
      });
    }

    if (dupSort === 'name') {
      filtered.sort((a, b) => a.label.localeCompare(b.label));
    } else {
      // Sort by country/section first, then by number.
      // `a.number - b.number` (the old impl) returned NaN for sticker
      // numbers that aren't plain integers — e.g. "FWC 12" — leaving
      // those items in undefined relative order. Now we extract the
      // trailing integer and fall back to localeCompare for ties.
      const numOf = (v) => {
        const m = String(v).match(/(\d+)\s*$/);
        return m ? parseInt(m[1], 10) : NaN;
      };
      filtered.sort((a, b) => {
        const catA = a.teamName || a.sectionName || '';
        const catB = b.teamName || b.sectionName || '';
        if (catA !== catB) return catA.localeCompare(catB);
        const nA = numOf(a.number);
        const nB = numOf(b.number);
        if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
        return String(a.number).localeCompare(String(b.number));
      });
    }

    return filtered;
  }, [allStickers, state.duplicates, state.trades, dupSearch, dupSort, getReservedCount]);

  const groupedDuplicates = useMemo(() => {
    if (dupSort === 'name') return null; // No grouping when sorting by name
    const groups = {};
    duplicateStickers.forEach(s => {
      const key = s.teamName || s.sectionName;
      if (!groups[key]) groups[key] = { name: key, flag: s.teamFlag, stickers: [] };
      groups[key].stickers.push(s);
    });
    return Object.values(groups);
  }, [duplicateStickers, dupSort]);

  const totalReserved = useMemo(() => {
    let count = 0;
    Object.values(state.trades || {}).forEach((t) => {
      if (!t || !t.reserved) return;
      const give = Array.isArray(t.give) ? t.give : [];
      count += give.length;
    });
    return count;
  }, [state.trades]);

  // Top-6 rare/legendary stickers the user is missing — used by Stats and
  // the ultrawide right rail. Computed once at this level so both reuse it.
  const missingLegendaries = useMemo(() => {
    return allStickers.filter(s => {
      const r = rarityMap?.[s.number];
      if (!(r !== undefined && r < 5)) return false;
      if (state.collected?.[s.number]) return false;
      // Si ya está apartada por un trato reservado, técnicamente no
      // "me hace falta" — está en camino. Sacarla de la vitrina de
      // Joyas Faltantes evita que el usuario sienta urgencia por algo
      // que ya tiene asegurado.
      if (isIncoming(s.number)) return false;
      return true;
    }).slice(0, 6);
  }, [allStickers, rarityMap, state.collected, isIncoming]);

  const handleJumpToSticker = (s) => {
    const stickerInfo = allStickers.find(st => st.number === s.number);
    if (!stickerInfo) return;
    const targetView = stickerInfo.team
      ? `group-${stickerInfo.group}`
      : `section-${stickerInfo.section}`;
    handleNav(targetView);
    setHighlightedSticker(s.number);
  };

  const searchResults = useMemo(() => {
    if (!search || search.length < 2) return [];
    // Fuzzy match against both the sticker number and label.
    // Accepts typos ("mesi" → Messi), partial words ("alva" → Álvarez),
    // and accent-free queries ("nunez" → Núñez).
    return fuzzyFilter(allStickers, search, s => [s.number, s.label], 15);
  }, [search, allStickers]);

  const toggleAllGroups = (forceState) => {
    const newState = {};
    if (forceState === 'collapse') {
      // groupedDuplicates is null when sorting by name. Guard so the
      // collapse-all button does not crash the whole page (which made
      // the user re-onboard / re-tour because the error tore down the
      // app tree).
      (groupedDuplicates || []).forEach(g => newState[g.name] = true);
    }
    setCollapsedGroups(newState);
  };

  const handleVisitFriend = (uid) => {
    // Navigate to duplicates view but set friendUid
    const url = new URL(window.location);
    url.searchParams.set('friend', uid);
    window.history.pushState({}, '', url);
    setPreviousView(activeView); // Remember where we were
    onClearFriend(uid); // This actually sets the friendUid in parent
  };

  useEffect(() => {
    if (user.uid) {
      // Public profile: name, photo, city, bio, lastSeen
      const unsubPublic = onSnapshot(
        doc(db, 'profiles', user.uid),
        (snap) => {
          const publicData = snap.exists()
            ? snap.data()
            : { name: user.displayName || user.email.split('@')[0], photo: user.photoURL || '', city: '' };
          setProfile(prev => ({ ...prev, ...publicData }));
          setProfileLoaded(true);
        },
        (err) => {
          console.error('Public profile snapshot failed:', err);
          // Mark as loaded even on error so the onboarding gate doesn't
          // wait forever. Worst case the user sees onboarding once;
          // better than a permanent spinner.
          setProfileLoaded(true);
        }
      );
      // Private profile: phone (and fcmToken, but we don't surface it in UI)
      const unsubPrivate = onSnapshot(
        doc(db, 'profiles_private', user.uid),
        (snap) => {
          const priv = snap.exists() ? snap.data() : {};
          setProfile(prev => ({ ...prev, phone: priv.phone || '' }));
        },
        (err) => console.error('Private profile snapshot failed:', err)
      );

      // Stamp lastSeen at most once every 5 minutes per user. Previously
      // every page reload / hot-reload fired a write — for a developer
      // iterating, that adds up fast. The activity indicator only
      // distinguishes "Activo ahora" / "Hoy" / "Hace Xd", so sub-5-min
      // precision is invisible to users.
      try {
        const stampKey = `lastSeenStamp_${user.uid}`;
        const last = Number(localStorage.getItem(stampKey) || 0);
        if (!last || Date.now() - last > 5 * 60 * 1000) {
          localStorage.setItem(stampKey, String(Date.now()));
          handleFirestoreWrite(
            () => updateDoc(doc(db, 'profiles', user.uid), { lastSeen: Date.now() }),
            { op: 'stampLastSeen' }
          ).catch(() => { /* already reported by handleFirestoreWrite */ });
        }
      } catch { /* localStorage disabled — fall through, skip the write */ }
      
      // Recibidas
      const qRec = query(collection(db, 'proposals'), where('toUid', '==', user.uid), where('status', '==', 'pending'));
      const unsubRec = onSnapshot(
        qRec,
        (snap) => {
          const list = [];
          snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
          if (prevProposalsCount.current !== null && list.length > prevProposalsCount.current) {
            const newest = list[list.length - 1];
            const fromName = newest?.fromName ?? 'Un coleccionista';
            showNotification(`¡Nueva propuesta de ${fromName}! ✨`, 'success');
            notifyNewProposal(fromName);
          }
          prevProposalsCount.current = list.length;
          setIncomingProposals(list);
        },
        (err) => console.error('Incoming proposals snapshot failed:', err)
      );

      // Enviadas
      const qSent = query(collection(db, 'proposals'), where('fromUid', '==', user.uid), where('status', '==', 'pending'));
      const unsubSent = onSnapshot(
        qSent,
        (snap) => {
          const list = snap.docs.map(doc => {
            const data = doc.data();
            // Fallback to friend name if toName is missing (for old proposals)
            if (!data.toName && state.friends && state.friends[data.toUid]) {
              data.toName = state.friends[data.toUid].name;
            }
            return { id: doc.id, ...data };
          });
          setOutgoingProposals(list);
        },
        (err) => console.error('Outgoing proposals snapshot failed:', err)
      );

      return () => { unsubPublic(); unsubPrivate(); unsubRec(); unsubSent(); };
    }
  }, [user.uid, user.displayName, user.email, user.photoURL]);

  const handleNav = (id, parentId = null) => {
    // If we are in a friend view, clear it when navigating via sidebar
    if (friendUid) onClearFriend(null);

    setPreviousView('stats'); // Reset previous view when using sidebar

    if (id.startsWith('team-') && parentId) {
      setActiveView(parentId);
      setSidebarOpen(false);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 50);
    } else {
      setActiveView(id);
      setSidebarOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchSelect = (sticker) => {
    // If we are in a friend view, clear it when selecting a search result
    if (friendUid) onClearFriend();

    const groupId = sticker.group ? `group-${sticker.group}` : `section-${sticker.section}`;
    setActiveView(groupId);
    setSearch('');
    
    // Highlight the sticker
    setHighlightedSticker(sticker.number);
    setTimeout(() => setHighlightedSticker(null), 3000);
    
    setTimeout(() => {
      // Find exact card to scroll to
      const el = document.getElementById(`sticker-${sticker.number}`);
      if (el) {
        // Center the sticker in the screen (offsetting header)
        const y = el.getBoundingClientRect().top + window.scrollY - 150;
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else if (sticker.team) {
        const teamEl = document.getElementById(`team-${sticker.team}`);
        if (teamEl) {
          const y = teamEl.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  const handleQuickAdd = (e) => {
    if (e) e.preventDefault();
    const val = normalizeStickerNumber(quickAdd);
    if (!val) return;

    const sticker = allStickers.find(s => {
      const q = val.replace(/\s+/g, '').toLowerCase();
      const stickerNumClean = String(s.number).replace(/\s+/g, '').toLowerCase();
      const stickerLabelClean = s.label.toLowerCase().replace(/\s+/g, '');
      return stickerNumClean === q || stickerLabelClean === q || stickerLabelClean.includes(q);
    });

    if (!sticker) {
      showNotification(`No se encontró la lámina "${quickAdd}"`, 'error');
      setQuickAdd('');
      return;
    }

    const isCollected = !!state.collected[sticker.number];
    if (!isCollected) {
      handleToggle(sticker.number);
      showNotification(`¡Lámina ${sticker.number} agregada!`, 'success');
    } else {
      const currentDups = state.duplicates[sticker.number] || 0;
      setDuplicates(sticker.number, currentDups + 1);
      showNotification(`¡Lámina ${sticker.number} es repetida! (Total: ${currentDups + 1})`, 'warning');
    }

    setQuickAdd('');
    
    // Also scroll to it and highlight it
    handleSearchSelect(sticker);
  };

  const handleStartTradeFromText = (receiveStickers, giveStickers = []) => {
    // Create an external friend
    const externalUid = `ext-${Date.now()}`;
    const name = `Amigo Texto ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    addFriend(externalUid, name);
    
    // Set up the trade
    setTrade(externalUid, {
      receive: receiveStickers.map(s => s.number),
      give: giveStickers.map(s => s.number),
      reserved: false
    });

    // Navigate to the trade view
    handleVisitFriend(externalUid);
    setShowTextMatcher(false);
    showNotification(`Se ha creado un intercambio con láminas.`, 'success');
  };

  const handleCopyDuplicates = () => {
    if (duplicateStickers.length === 0) {
      showNotification('No tienes repetidas para copiar.', 'warning');
      return;
    }

    // Build groups on-the-fly so the copy works even when the current
    // sort is "name" (groupedDuplicates is null in that case).
    const groupsForCopy = (() => {
      if (groupedDuplicates) return groupedDuplicates;
      const acc = {};
      duplicateStickers.forEach(s => {
        const key = s.teamName || s.sectionName || 'Otros';
        if (!acc[key]) acc[key] = { name: key, stickers: [] };
        acc[key].stickers.push(s);
      });
      return Object.values(acc);
    })();

    let text = '';
    groupsForCopy.forEach(group => {
      // Find the team code if it's a team, or use the section name
      const code = group.stickers[0].team || group.name;
      const list = group.stickers.map(s => `${s.number.split(' ').pop()}(${s.dupes}x)`).join(', ');
      text += `${code}: ${list}\n`;
    });

    navigator.clipboard.writeText(text.trim());
    showNotification('¡Lista de repetidas copiada al portapapeles!', 'success');
  };

  const renderDuplicates = () => {
    if (friendUid && !dataLoading) {
      return (
        <TradeMatch 
          myState={state} 
          friendUid={friendUid} 
          onClose={onClearFriend} 
          onAddFriend={addFriend}
          onRemoveFriend={removeFriend}
          isFriend={!!(state.friends && state.friends[friendUid])}
          toggleSticker={toggleSticker}
          setDuplicates={setDuplicates}
          setTrade={setTrade}
          removeTrade={removeTrade}
          getReservedCount={getReservedCount}
          completeTrade={completeTrade}
        />
      );
    }

    if (dataLoading) {
      return <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:'100px',color:'var(--text-muted)'}}>Cargando tu álbum de la nube...</div>;
    }

    const totalDups = duplicateStickers.reduce((sum, s) => sum + s.dupes, 0);
    const totalReservedDups = duplicateStickers.reduce((sum, s) => sum + s.reservedCount, 0);
    const totalAvailable = totalDups - totalReservedDups;
    // groupedDuplicates is null when sorting by name — there are no
    // groups to collapse in that case, so "all collapsed" is vacuously
    // false (the collapse-all chip stays in "expand" state).
    const isAllCollapsed = duplicateStickers.length > 0 && !!groupedDuplicates && groupedDuplicates.every(g => collapsedGroups[g.name]);

    return (
      <div>
        <div className="section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2>🔄 Láminas Repetidas</h2>
              <p className="subtitle">
                {duplicateStickers.length} lámina{duplicateStickers.length !== 1 ? 's' : ''} con repetidas — {totalDups} en total
                {totalReservedDups > 0 && (
                  <>
                    {' · '}
                    <span style={{ color: '#d97706', fontWeight: 600 }}>🔒 {totalReservedDups} reservada{totalReservedDups !== 1 ? 's' : ''}</span>
                    {' · '}
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{totalAvailable} disponible{totalAvailable !== 1 ? 's' : ''}</span>
                  </>
                )}
              </p>
              {totalReservedDups > 0 && (
                <button
                  className="btn"
                  style={{ marginTop: 8, fontSize: '0.82rem', padding: '6px 12px', color: '#fbbf24', borderColor: 'rgba(217,119,6,0.4)' }}
                  onClick={async () => {
                    const ok = await confirm(`¿Liberar todas las reservas? Esto va a desbloquear las ${totalReservedDups} láminas reservadas. Tus tratos pendientes quedan guardados, pero las láminas vuelven a estar disponibles para otros canjes.`);
                    if (!ok) return;
                    const cleared = clearAllReservations();
                    showNotification(cleared > 0
                      ? `${cleared} trato${cleared !== 1 ? 's' : ''} liberado${cleared !== 1 ? 's' : ''}.`
                      : 'No había reservas para liberar.', 'success');
                  }}
                >
                  🔓 Liberar todas las reservas
                </button>
              )}
            </div>
            {dupSort === 'country' && duplicateStickers.length > 0 && (
              <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                <button className="btn" onClick={() => setShowTextMatcher(true)}>
                  🔍 Analizar Texto
                </button>
                <button className="btn btn-gold" onClick={handleCopyDuplicates}>
                  📋 Copiar Lista
                </button>
                <button className="btn" onClick={() => toggleAllGroups(isAllCollapsed ? 'expand' : 'collapse')}>
                  {isAllCollapsed ? '➕ Expandir Todo' : '➖ Contraer Todo'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="dup-toolbar">
          <div className="dup-search">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              placeholder="Buscar en tus repetidas..." 
              value={dupSearch}
              onChange={(e) => setDupSearch(e.target.value)}
            />
          </div>
          <div className="dup-sort">
            <span>Ordenar por:</span>
            <select value={dupSort} onChange={(e) => setDupSort(e.target.value)}>
              <option value="country">País / Sección</option>
              <option value="name">Nombre del jugador</option>
            </select>
          </div>
        </div>

        {duplicateStickers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>📭</div>
            <p style={{ fontSize: '1.1rem' }}>No se encontraron láminas repetidas</p>
            <p style={{ fontSize: '0.85rem', marginTop: 8, color: 'var(--text-muted)' }}>Ajusta tu búsqueda o marca repetidas en tus láminas</p>
          </div>
        ) : dupSort === 'country' ? (
          <div>
            {groupedDuplicates.map(group => {
              const isCollapsed = collapsedGroups[group.name];
              return (
                <div key={group.name} style={{ marginBottom: isCollapsed ? '8px' : '0' }}>
                  <div 
                    className="dup-group-header" 
                    onClick={() => setCollapsedGroups(prev => ({ ...prev, [group.name]: !prev[group.name] }))}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="expand-icon" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0)' }}>▼</span>
                    {group.flag && <span className="flag"><Twemoji emoji={group.flag} /></span>}
                    <h3>{group.name}</h3>
                    <span className="count">{group.stickers.length} láminas</span>
                  </div>
                  {!isCollapsed && (
                    <div className={`sticker-grid${gridLayout === 'list' ? ' compact' : ''}`}>
                      {group.stickers.map(s => (
                        <ReservableStickerWrap
                          key={s.number}
                          sticker={s}
                          state={state}
                          rarityMap={rarityMap}
                          handleToggle={handleToggle}
                          setDuplicates={setDuplicates}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="sticker-grid">
            {duplicateStickers.map(s => (
              <ReservableStickerWrap
                key={s.number}
                sticker={s}
                state={state}
                rarityMap={rarityMap}
                handleToggle={handleToggle}
                setDuplicates={setDuplicates}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (friendUid && !dataLoading) {
      return (
        <TradeMatch 
          myState={state} 
          userUid={user?.uid}
          friendUid={friendUid} 
          onClose={() => {
            onClearFriend(null);
            if (previousView) setActiveView(previousView);
          }} 
          onAddFriend={addFriend}
          onRemoveFriend={removeFriend}
          isFriend={!!(state.friends && state.friends[friendUid])}
          toggleSticker={toggleSticker}
          setDuplicates={setDuplicates}
          setTrade={setTrade}
          removeTrade={removeTrade}
          getReservedCount={getReservedCount}
          completeTrade={completeTrade}
          rarityMap={rarityMap}
        />
      );
    }
    if (dataLoading) {
      return <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:'100px',color:'var(--text-muted)'}}>Cargando tu álbum de la nube...</div>;
    }
    
    if (activeView === 'stats') return renderStats();
    if (activeView === 'profile') return <ProfileView user={user} profile={profile} updateProfile={updateProfile} exportAllMyData={exportAllMyData} deleteMyAccount={deleteMyAccount} />;
    if (activeView === 'community') return (
      <Community
        user={user}
        myState={state}
        allStickers={allStickers}
        onVisitFriend={handleVisitFriend}
        showNotification={showNotification}
        incomingProposals={incomingProposals}
        outgoingProposals={outgoingProposals}
        rarityMap={rarityMap}
        profile={profile}
      />
    );
    if (activeView === 'proposals') return (
      <ProposalsView
        incoming={incomingProposals}
        outgoing={outgoingProposals}
        myUid={user.uid}
        trustScores={trustScores}
        onVisitFriend={handleVisitFriend}
        onRemove={async (id, otherUid) => {
          try {
            await handleFirestoreWrite(
              () => deleteDoc(doc(db, 'proposals', id)),
              { op: 'removeProposal', proposalId: id }
            );
            removeTrade(otherUid);
          } catch (err) {
            console.error('Error removing proposal:', err);
            showNotification('No pudimos eliminar la propuesta. Inténtalo de nuevo.', 'error');
          }
        }}
        onSync={async (proposal) => {
          try {
            const otherUid = proposal.fromUid === user.uid ? proposal.toUid : proposal.fromUid;
            
            // 1. Fetch other user's inventory
            const otherSnap = await getDoc(doc(db, 'users', otherUid));
            if (!otherSnap.exists()) {
              showNotification('No se pudo encontrar el inventario del otro coleccionista.', 'error');
              return;
            }
            const otherState = otherSnap.data();

            // 2. Identify what I am receiving and what I am giving
            const isSender = proposal.fromUid === user.uid;
            const myReceiveList = isSender ? (proposal.receive || []) : (proposal.give || []);
            const myGiveList = isSender ? (proposal.give || []) : (proposal.receive || []);

            // 3. Filter against CURRENT inventories
            // - What I receive must be in their duplicates and NOT in my collected
            const newMyReceiveList = myReceiveList.filter(n => (otherState.duplicates?.[n] > 0) && !state.collected[n]);
            
            // - What I give must be in my duplicates and NOT in their collected
            const newMyGiveList = myGiveList.filter(n => (state.duplicates[n] > 0) && !otherState.collected?.[n]);

            // 4. Check for changes
            if (newMyReceiveList.length === myReceiveList.length && newMyGiveList.length === myGiveList.length) {
              showNotification('La propuesta ya está al día con los inventarios actuales.', 'info');
              return;
            }

            // 5. Update proposal
            const updateObj = {
              modifiedBySystem: true,
              systemMessage: '🔄 Propuesta sincronizada manualmente con los inventarios actuales.'
            };
            
            if (isSender) {
              updateObj.receive = newMyReceiveList;
              updateObj.give = newMyGiveList;
            } else {
              updateObj.give = newMyReceiveList;
              updateObj.receive = newMyGiveList;
            }

            // Cancel if both arrays become empty
            if (newMyReceiveList.length === 0 && newMyGiveList.length === 0) {
              updateObj.status = 'cancelled';
              updateObj.systemMessage = '🚫 Propuesta cancelada automáticamente porque las láminas ya no están disponibles en ningún inventario.';
            }

            await handleFirestoreWrite(
              () => updateDoc(doc(db, 'proposals', proposal.id), updateObj),
              { op: 'syncProposal', proposalId: proposal.id }
            );
            showNotification('¡Propuesta sincronizada correctamente! ✨', 'success');

          } catch (err) {
            console.error("Error syncing proposal:", err);
            showNotification('Error al sincronizar la propuesta.', 'error');
          }
        }}
      />
    );
    if (activeView === 'achievements') return <AchievementsView state={state} rarityMap={rarityMap} profile={profile} />;
    if (activeView === 'history') return <HistoryView state={state} />;
    if (activeView === 'rarity') return <RarityView allStickers={allStickers} />;
    if (activeView === 'reconcile') return <ReconcileView appDuplicates={state.duplicates} onSync={syncAllDuplicates} allStickers={allStickers} />;
    if (activeView === 'friends') return <FriendsView friends={state.friends} myState={state} allStickers={allStickers} onVisitFriend={handleVisitFriend} onRemoveFriend={removeFriend} onAddFriend={addFriend} userId={user.uid} trustScores={trustScores} sortedFriendsList={sortedFriendsList} loadingStats={loadingFriendsStats} />;
    if (activeView === 'duplicates') return renderDuplicates();
    if (activeView.startsWith('section-')) {
      const sectionId = activeView.replace('section-', '');
      const section = albumData.sections.find(s => s.id === sectionId);
      if (!section) return null;
      const progress = getProgress(section.stickers);
      const counts = computeGridCounts(section.stickers, state);
      const visible = applyGridFilter(section.stickers, gridFilter, state);
      return (
        <div>
          <div className="section-header">
            <h2>{section.icon} {section.name}</h2>
            <div className="section-progress">
              <ProgressBar pct={progress.pct} width="300px" />
              <span><strong>{progress.have}</strong> / {progress.total} ({progress.pct}%)</span>
            </div>
          </div>
          <GridFilterBar
            value={gridFilter}
            onChange={setGridFilter}
            counts={counts}
            layout={gridLayout}
            onLayoutChange={(next) => { setGridLayout(next); safeStorage.set('gridLayout', next); }}
          />
          {visible.length === 0 ? (
            <div className="grid-empty-state">
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🎉</div>
              <p>No hay láminas que coincidan con este filtro.</p>
              <button className="btn" onClick={() => setGridFilter('all')}>Ver todas</button>
            </div>
          ) : (
            <div className={`sticker-grid${gridLayout === 'list' ? ' compact' : ''}`}>
              {visible.map(s => (
                <StickerCard
                  key={s.number} sticker={s}
                  collected={!!state.collected[s.number]}
                  duplicates={state.duplicates[s.number]}
                  onToggle={handleToggle} onDupChange={setDuplicates}
                  isHighlighted={highlightedSticker === s.number}
                  rarity={rarityMap?.[s.number]}
                  incoming={isIncoming(s.number)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    if (activeView.startsWith('group-')) {
      const groupId = activeView.replace('group-', '');
      const group = albumData.groups.find(g => g.id === groupId);
      if (!group) return null;
      const allGroupStickers = group.teams.flatMap(t => t.stickers);
      const progress = getProgress(allGroupStickers);
      const counts = computeGridCounts(allGroupStickers, state);
      const visibleTeams = group.teams
        .map(t => ({ team: t, visible: applyGridFilter(t.stickers, gridFilter, state) }))
        .filter(x => x.visible.length > 0);
      return (
        <div>
          <div className="section-header">
            <h2>⚽ {group.name}</h2>
            <p className="subtitle">
              {group.teams.map((t, i) => (
                <span key={t.code}>
                  {i > 0 && ' · '}
                  <Twemoji emoji={t.flag} /> {t.name}
                </span>
              ))}
            </p>
            <div className="section-progress">
              <ProgressBar pct={progress.pct} width="300px" />
              <span><strong>{progress.have}</strong> / {progress.total} ({progress.pct}%)</span>
            </div>
          </div>
          <GridFilterBar
            value={gridFilter}
            onChange={setGridFilter}
            counts={counts}
            layout={gridLayout}
            onLayoutChange={(next) => { setGridLayout(next); safeStorage.set('gridLayout', next); }}
          />
          {visibleTeams.length === 0 ? (
            <div className="grid-empty-state">
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🎉</div>
              <p>No hay láminas que coincidan con este filtro en este grupo.</p>
              <button className="btn" onClick={() => setGridFilter('all')}>Ver todas</button>
            </div>
          ) : (
            group.teams.map(t => (
              <TeamBlock
                key={t.code} team={t}
                collected={state.collected || {}} duplicates={state.duplicates || {}}
                onToggle={handleToggle} onDupChange={setDuplicates}
                getProgress={getProgress}
                highlightedSticker={highlightedSticker}
                rarityMap={rarityMap}
                gridFilter={gridFilter}
                gridLayout={gridLayout}
                state={state}
                isIncoming={isIncoming}
              />
            ))
          )}
        </div>
      );
    }
    return null;
  };


  const renderStats = () => {
    const groupStats = albumData.groups.map(g => {
      const stickers = g.teams.flatMap(t => t.stickers);
      const prog = getProgress(stickers);
      // Per-group breakdown: missing + duplicates so the user sees what's actionable
      let missing = 0, dupes = 0;
      stickers.forEach(s => {
        if (!state.collected?.[s.number]) missing++;
        if ((state.duplicates?.[s.number] || 0) > 0) dupes++;
      });
      // Top-3 teams by missing count → "where to focus"
      const teamMissing = g.teams.map(t => {
        const m = t.stickers.filter(s => !state.collected?.[s.number]).length;
        return { ...t, missing: m };
      }).sort((a, b) => b.missing - a.missing).filter(t => t.missing > 0).slice(0, 3);
      return { ...g, ...prog, missing, dupes, teamMissing };
    });

    return (
      <div>
        <div className="section-header">
          <h2>📊 Resumen del Álbum</h2>
          <p className="subtitle">FIFA World Cup 2026™ — Colección Panini</p>
        </div>
        <StaleReservationsBanner
          state={state}
          userUid={user.uid}
          onVisitFriend={handleVisitFriend}
          onLiberar={(friendUid) => {
            const t = state.trades?.[friendUid];
            if (!t) return;
            setTrade(friendUid, {
              receive: t.receive || [],
              give: t.give || [],
              reserved: false,
              note: t.note || '',
            }, { immediate: true });
            showNotification('🔓 Reserva liberada. Las láminas vuelven a estar disponibles.', 'success');
          }}
        />
        <TradeSuggestionsBanner
          friendsList={sortedFriendsList}
          trades={state.trades}
          userUid={user.uid}
          onVisitFriend={handleVisitFriend}
        />
        <div className="summary-grid">
          <div className="stat-card summary-card-main">
            <div className="stat-label">Progreso Total</div>
            <div className="stat-value">{totalProgress.pct}%</div>
            <div className="stat-sub" style={{ marginBottom: '16px' }}>{totalProgress.have} de {totalProgress.total} láminas</div>
            <ProgressBar pct={totalProgress.pct} width="100%" />
          </div>
          <div className="stat-card">
            <div className="stat-label">Láminas Obtenidas</div>
            <div className="stat-value">{totalProgress.have}</div>
            <div className="stat-sub">de {totalProgress.total} totales</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Láminas Faltantes</div>
            <div className="stat-value">{totalProgress.total - totalProgress.have}</div>
            <div className="stat-sub">por conseguir</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Repetidas {totalReserved > 0 ? 'disponibles' : ''}</div>
            <div className="stat-value">{Math.max(0, totalDuplicates - totalReserved)}</div>
            <div className="stat-sub">
              {totalReserved > 0
                ? `de ${totalDuplicates} totales · ${totalReserved} reservada${totalReserved !== 1 ? 's' : ''}`
                : 'para intercambio'}
            </div>
          </div>
          {totalReserved > 0 && (
            <div className="stat-card stat-danger">
              <div className="stat-label">Reservadas</div>
              <div className="stat-value">{totalReserved}</div>
              <div className="stat-sub">para tratos pendientes</div>
            </div>
          )}
        </div>

        <AlbumStatsCharts
          allStickers={allStickers}
          collected={state.collected}
          duplicates={state.duplicates}
        />

        {missingLegendaries.length > 0 && (
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '1rem' }}>💎 Joyas Faltantes (Legendarias)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
              {missingLegendaries.map(s => (
                <div key={s.number} className="stat-card legendary-glow" style={{ padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => {
                  const sectionId = s.team ? `group-${allStickers.find(st => st.number === s.number).group}` : `section-${s.section}`;
                  handleNav(sectionId);
                  setHighlightedSticker(s.number);
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--accent-gold)', fontWeight: 800 }}>{s.number}</div>
                  <div style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{s.teamName || s.sectionName}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '1rem' }}>Progreso por Grupo</h3>
        <div className="group-stats-grid">
          {groupStats.map(g => {
            const isComplete = g.pct === 100;
            return (
              <div
                key={g.id}
                className={`group-stat-card ${isComplete ? 'group-stat-complete' : ''}`}
                onClick={() => handleNav(`group-${g.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNav(`group-${g.id}`); } }}
                role="button"
                tabIndex={0}
                aria-label={`${g.name}: ${g.pct}% completo, ${g.missing} faltan, ${g.dupes} repetidas`}
              >
                <div className="group-name">
                  <span>{g.name}</span>
                  <span style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    color: isComplete ? '#4ade80' : g.pct >= 75 ? 'var(--accent-gold)' : g.pct >= 40 ? '#f0d078' : 'var(--text-muted)'
                  }}>
                    {isComplete ? '✓ 100%' : `${g.pct}%`}
                  </span>
                </div>
                <div className="group-teams">
                  {g.teams.map((t, i) => (
                    <span key={t.code}>
                      {i > 0 && ' · '}
                      <Twemoji emoji={t.flag} /> {t.name}
                    </span>
                  ))}
                </div>

                {/* Mini-stats row */}
                <div className="group-stat-pills">
                  {g.missing > 0 ? (
                    <button
                      className="group-stat-pill group-stat-pill-missing"
                      onClick={(e) => { e.stopPropagation(); setGridFilter('missing'); handleNav(`group-${g.id}`); }}
                      aria-label={`Ver las ${g.missing} láminas que me faltan en ${g.name}`}
                    >
                      <span>🎯</span><strong>{g.missing}</strong> faltan
                    </button>
                  ) : (
                    <span className="group-stat-pill group-stat-pill-done">
                      <span>✨</span>Completo
                    </span>
                  )}
                  {g.dupes > 0 && (
                    <button
                      className="group-stat-pill group-stat-pill-dupes"
                      onClick={(e) => { e.stopPropagation(); setGridFilter('duplicates'); handleNav(`group-${g.id}`); }}
                      aria-label={`Ver las ${g.dupes} repetidas que tengo en ${g.name}`}
                    >
                      <span>🔄</span><strong>{g.dupes}</strong> repe
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto' }}>
                  <ProgressBar pct={g.pct} width="100%" />
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{g.have}/{g.total}</div>
                </div>

                {/* "Focus on" — top 3 teams with most missing */}
                {g.teamMissing.length > 0 && !isComplete && (
                  <div className="group-stat-focus">
                    <span className="group-stat-focus-label">Concéntrate en:</span>
                    {g.teamMissing.map(t => (
                      <span key={t.code} className="group-stat-focus-team">
                        <Twemoji emoji={t.flag} /> {t.name} <span style={{ color: '#60a5fa', fontWeight: 700 }}>·{t.missing}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '40px' }}>
          <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '1rem' }}>Última Actividad 🕒</h3>
          {state.history && state.history.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {state.history.map(item => (
                <div key={item.id} className="stat-card" style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.01)' }}>
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                      {item.type === 'trade' ? `🤝 Intercambio con ${item.friendName}` : '✨ Acción realizada'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Recibiste {item.receive} · Entregaste {item.give}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {new Date(item.date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="stat-card" style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', borderStyle: 'dashed', background: 'transparent' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>🤝</div>
              <div style={{ fontSize: '0.9rem' }}>Aún no tienes actividad reciente.</div>
              <div style={{ fontSize: '0.75rem' }}>Completa un intercambio para ver tu historial aquí.</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <OfflineBanner />
      <AdBlockerBanner />
      <FirestoreErrorBanner />
      <header className="header">
        <div className="header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="mobile-nav-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div className="header-brand">
              <span className="trophy">🏆</span>
              <h1>Álbum Mundial 2026</h1>
            </div>
            <form onSubmit={handleQuickAdd} className="search-box quick-add" style={{ marginLeft: '10px', width: '200px' }}>
              <span className="search-icon">✨</span>
              <input
                type="text" placeholder="Registro rápido..."
                value={quickAdd} onChange={e => setQuickAdd(e.target.value)}
              />
            </form>
          </div>
          <div className="header-stats">
            <div className="header-stat">
              <span>Progreso:</span>
              <strong>{totalProgress.pct}%</strong>
              <ProgressBar pct={totalProgress.pct} width="120px" />
            </div>
            <div
              className="header-stat"
              title={totalReserved > 0
                ? `${totalDuplicates - totalReserved} disponibles para intercambiar (${totalDuplicates} totales − ${totalReserved} ya reservadas)`
                : `${totalDuplicates} láminas repetidas`}
            >
              <span>🔄</span>
              <strong>{Math.max(0, totalDuplicates - totalReserved)}</strong>
              <span>{totalReserved > 0 ? 'disponibles' : 'repetidas'}</span>
            </div>
          </div>
          <div className="header-right-group">
            {totalReserved > 0 && (
              <div
                className="header-stat header-stat-reserved"
                title={`${totalReserved} lámina${totalReserved !== 1 ? 's' : ''} reservada${totalReserved !== 1 ? 's' : ''} para intercambios. Hazle click para ver.`}
                onClick={() => handleNav('duplicates')}
                role="button"
                tabIndex={0}
              >
                <span aria-hidden="true">🔒</span>
                <strong>{totalReserved}</strong>
                <span className="header-stat-label">reservadas</span>
              </div>
            )}
            <div className="search-box" data-tour="header-search">
              <span className="search-icon">🔍</span>
              <input
                type="text" placeholder="Buscar lámina..."
                value={search} onChange={e => setSearch(e.target.value)}
              />
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(s => {
                    const rarity = rarityMap?.[s.number];
                    const isLegendary = rarity !== undefined && rarity < 5;
                    const isRare = rarity !== undefined && rarity < 15 && rarity >= 5;
                    const hasDup = state.duplicates?.[s.number] > 0;
                    const isCollected = !!state.collected?.[s.number];
                    
                    return (
                      <div key={s.number} className="search-result-item" onClick={() => handleSearchSelect(s)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <span className={`sr-number ${isLegendary ? 'legendary-glow' : isRare ? 'rare-glow' : ''}`}>
                            {s.number}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '0.9rem' }}>{s.label}</span>
                            <span className="sr-team" style={{ fontSize: '0.7rem' }}>{s.teamName || s.sectionName}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          {isCollected && <span title="Ya la tienes" style={{ fontSize: '0.8rem' }}>✅</span>}
                          {hasDup && <span title="Tienes repetidas" style={{ fontSize: '0.8rem' }}>🔄</span>}
                          {isLegendary && <span style={{ fontSize: '0.65rem', background: 'var(--accent-gold)', color: '#1a1a2e', padding: '1px 4px', borderRadius: '3px', fontWeight: 800 }}>LEG</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="header-actions">
              {/* Primary actions — always visible */}
              <button className="btn btn-gold" onClick={() => setShowQRModal(true)} aria-label="Mostrar código QR de mi álbum" data-tour="header-qr">📱 Mi QR</button>
              <button className="btn header-action-desktop" onClick={() => setShowShareImage(true)} aria-label="Compartir colección como imagen">📸 Compartir</button>
              <button className="btn header-action-desktop" onClick={() => { const url = `${window.location.origin}/?friend=${user.uid}`; navigator.clipboard.writeText(url); showNotification('¡Enlace de intercambio copiado!', 'success'); }} aria-label="Copiar enlace de mi álbum" data-tour="share-link">🔗 Link</button>

              {/* Overflow menu — visible on all screens, secondary actions inside */}
              <div className="header-overflow-menu" ref={overflowRef}>
                <button className="btn" onClick={() => setOverflowOpen(v => !v)} aria-label="Más opciones" aria-haspopup="true">⋯</button>
                {overflowOpen && (
                  <div className="header-overflow-dropdown">
                    <button className="overflow-item" onClick={() => { setShowInPersonTrade(true); setOverflowOpen(false); }}>🤝 Intercambio presencial</button>
                    <button className="overflow-item header-action-mobile-only" onClick={() => { setShowShareImage(true); setOverflowOpen(false); }}>📸 Compartir imagen</button>
                    <button className="overflow-item header-action-mobile-only" onClick={() => { const url = `${window.location.origin}/?friend=${user.uid}`; navigator.clipboard.writeText(url); showNotification('¡Enlace copiado!', 'success'); setOverflowOpen(false); }}>🔗 Copiar enlace</button>
                    <div className="overflow-divider" />
                    <button className="overflow-item" onClick={() => {
                      const firstGroup = albumData.groups[0];
                      if (firstGroup) setActiveView(`group-${firstGroup.id}`);
                      setOverflowOpen(false);
                      setTimeout(() => setShowTour(true), 400);
                    }}>❓ Ver tour de bienvenida</button>
                    <button className="overflow-item" onClick={() => { exportData(); setOverflowOpen(false); }}>📥 Exportar JSON</button>
                    <button className="overflow-item" onClick={() => { fileRef.current?.click(); setOverflowOpen(false); }}>📤 Importar JSON</button>
                    <div className="overflow-divider" />
                    <button className="overflow-item overflow-danger" onClick={() => { resetAll(); setOverflowOpen(false); }}>🗑️ Reiniciar álbum</button>
                    <button className="overflow-item" onClick={() => { signOut(auth); setOverflowOpen(false); }}>🚪 Cerrar sesión</button>
                  </div>
                )}
              </div>

              <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) importData(e.target.files[0]); }} />
            </div>
          </div>
        </div>
      </header>

      {notifSupported && currentPermission === 'default' && !notifBannerDismissed && !showOnboarding && (
        <div className="notif-banner">
          <span className="notif-banner-icon">🔔</span>
          <span className="notif-banner-text">Activa las notificaciones para saber cuando lleguen propuestas de intercambio</span>
          <button
            className="btn notif-banner-btn-accept"
            onClick={async () => {
              const result = await requestPermission();
              if (result !== 'default') {
                setNotifBannerDismissed(true);
                safeStorage.set('notifBannerDismissed', '1');
              }
            }}
          >
            Activar
          </button>
          <button
            className="btn notif-banner-btn-dismiss"
            aria-label="Cerrar aviso"
            onClick={() => {
              setNotifBannerDismissed(true);
              safeStorage.set('notifBannerDismissed', '1');
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="app-layout">
        <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
        <nav className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-tour="sidebar-nav">
          <div className="sidebar-content">
            {/* Acceso rápido al modo presencial. Es una ACCIÓN (abre
                modal), no una ruta, por eso vive arriba de navItems con
                estilo destacado para que se note a primera vista. */}
            <div className="sidebar-section">
              <div
                className="sidebar-item sidebar-item-action"
                onClick={() => { setShowInPersonTrade(true); setSidebarOpen(false); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowInPersonTrade(true); setSidebarOpen(false); } }}
                style={{
                  background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.2), rgba(212, 168, 67, 0.08))',
                  border: '1px solid rgba(212, 168, 67, 0.45)',
                  borderRadius: '10px',
                  margin: '4px 0 10px',
                  fontWeight: 700,
                }}
              >
                <span className="icon">🤝</span>
                <span>Intercambio presencial</span>
              </div>
            </div>
            {navItems.map(item => (
              <div key={item.id} className="sidebar-section">
                <div
                  className={`sidebar-item ${activeView === item.id ? 'active' : ''}`}
                  onClick={() => handleNav(item.id)}
                  onMouseEnter={() => prefetchView(item.id)}
                  onFocus={() => prefetchView(item.id)}
                >
                  <span className="icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge && <span className="badge-notification">{item.badge}</span>}
                  {item.teams && (
                    <span className="badge">
                      {(() => { const p = getProgress(albumData.groups.find(g => `group-${g.id}` === item.id)?.teams.flatMap(t => t.stickers) || []); return `${p.have}/${p.total}`; })()}
                    </span>
                  )}
                </div>
                {item.teams && activeView === item.id && item.teams.map(t => (
                  <div key={t.id} className="sidebar-item sidebar-team" onClick={(e) => { e.stopPropagation(); handleNav(t.id, item.id); }}>
                    <span><Twemoji emoji={t.flag} /> {t.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          <div className="sidebar-footer mobile-only">
            <div className="sidebar-divider"></div>
            <button className="sidebar-item" onClick={() => { setShowQRModal(true); setSidebarOpen(false); }}>
              <span className="icon">📱</span>
              <span>Mi QR</span>
            </button>
            <button className="sidebar-item" onClick={() => {
              const url = `${window.location.origin}/?friend=${user.uid}`;
              navigator.clipboard.writeText(url);
              showNotification('¡Enlace copiado!', 'success');
              setSidebarOpen(false);
            }}>
              <span className="icon">🔗</span>
              <span>Compartir Álbum</span>
            </button>
            <button className="sidebar-item" onClick={() => { exportData(); setSidebarOpen(false); }}>
              <span className="icon">📥</span>
              <span>Exportar JSON</span>
            </button>
            <button className="sidebar-item" onClick={() => { fileRef.current?.click(); setSidebarOpen(false); }}>
              <span className="icon">📤</span>
              <span>Importar JSON</span>
            </button>
            <button className="sidebar-item btn-danger-text" onClick={() => { resetAll(); setSidebarOpen(false); }}>
              <span className="icon">🗑️</span>
              <span>Reiniciar Álbum</span>
            </button>
            <button className="sidebar-item" onClick={() => { signOut(auth); setSidebarOpen(false); }}>
              <span className="icon">🚪</span>
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </nav>

        <main className="main-content">
          <div className="group-tabs">
            <div className={`group-tab ${activeView === 'stats' ? 'active' : ''}`} onClick={() => handleNav('stats')}>📊</div>
            <div className={`group-tab ${activeView === 'community' ? 'active' : ''}`} onClick={() => handleNav('community')}>🌍</div>
            <div className={`group-tab ${activeView === 'duplicates' ? 'active' : ''}`} onClick={() => handleNav('duplicates')}>🔄</div>
            {albumData.sections.map(s => (
              <div key={s.id} className={`group-tab ${activeView === `section-${s.id}` ? 'active' : ''}`} onClick={() => handleNav(`section-${s.id}`)}>{s.icon}</div>
            ))}
            {albumData.groups.map(g => (
              <div key={g.id} className={`group-tab ${activeView === `group-${g.id}` ? 'active' : ''}`} onClick={() => handleNav(`group-${g.id}`)}>
                {g.id}
              </div>
            ))}
          </div>
          <div key={activeView} className="view-enter">
            <Suspense fallback={<RouteFallback />}>
              {renderContent()}
            </Suspense>
          </div>
        </main>

        <UltrawideAside
          totalProgress={totalProgress}
          totalDuplicates={totalDuplicates}
          totalReserved={totalReserved}
          missingLegendaries={missingLegendaries}
          incomingProposals={incomingProposals}
          history={state.history}
          onNavigateToProposals={() => handleNav('proposals')}
          onJumpToSticker={handleJumpToSticker}
        />
      </div>

      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal-content" onClick={e => e.stopPropagation()}>
            <h3>📱 Intercambio Rápido</h3>
            <p>Muestra este código a otro coleccionista para que pueda ver tus láminas repetidas.</p>
            <div className="qr-container">
              <QRCodeSVG 
                value={`${window.location.origin}/?friend=${user.uid}`} 
                size={220} 
                bgColor={"transparent"} 
                fgColor={"#f0f0f8"} 
                level={"Q"} 
              />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button className="btn" style={{ flex: 1, padding: '10px' }} onClick={() => {
                navigator.clipboard.writeText(user.uid);
                showNotification('ID copiado al portapapeles', 'success');
              }}>
                📋 Copiar ID
              </button>
              <button className="btn btn-gold" style={{ flex: 1, padding: '10px' }} onClick={() => setShowQRModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showTextMatcher && (
        <Suspense fallback={null}>
          <TextMatcher
            myCollected={state.collected}
            myDuplicates={state.duplicates}
            allStickers={allStickers}
            onClose={() => setShowTextMatcher(false)}
            onStartTrade={handleStartTradeFromText}
          />
        </Suspense>
      )}

      {celebration && (
        <CelebrationOverlay message={celebration} onFinished={() => setCelebration(null)} />
      )}

      {showShareImage && (
        <Suspense fallback={null}>
          <ShareImageModal
            profile={profile}
            state={state}
            totalProgress={totalProgress}
            totalDuplicates={totalDuplicates}
            allStickers={allStickers}
            onClose={() => setShowShareImage(false)}
          />
        </Suspense>
      )}

      {showInPersonTrade && !dataLoading && (
        <Suspense fallback={null}>
          <InPersonTradeView
            state={state}
            getReservedCount={getReservedCount}
            addFriend={addFriend}
            setTrade={setTrade}
            completeTrade={completeTrade}
            onClose={() => setShowInPersonTrade(false)}
          />
        </Suspense>
      )}

      {showOnboarding && !dataLoading && (
        <Suspense fallback={null}>
        <OnboardingModal
          user={user}
          allStickers={allStickers}
          onUpdateProfile={updateProfile}
          onMarkStickers={markStickers}
          onFinish={() => {
            safeStorage.set(`onboardingDone_${user.uid}`, '1');
            setShowOnboarding(false);
            // Right after setup: trigger the product tour (only once)
            if (!safeStorage.get(`productTour_${user.uid}`)) {
              // Navigate to the first group so the grid-filter target exists
              // when the tour reaches that step
              const firstGroup = albumData.groups[0];
              if (firstGroup) setActiveView(`group-${firstGroup.id}`);
              // Defer so the main UI paints before we measure it
              setTimeout(() => setShowTour(true), 600);
            }
          }}
        />
        </Suspense>
      )}

      {showTour && (
        <Suspense fallback={null}>
        <ProductTour
          steps={[
            { id: 'header-search', title: 'Busca cualquier lámina 🔍',
              body: 'Escribe un número (ej. "087") o nombre. Te lleva directo a la lámina, esté en cualquier grupo.' },
            { id: 'grid-filter', title: 'Filtra qué quieres ver 🎚️',
              body: 'Cambia entre "Todas", "Me faltan", "Repetidas" y "Tengo". La barra se queda arriba mientras haces scroll.' },
            { id: 'header-qr', title: '¿Te encuentras con alguien? 📱',
              body: 'Tu código QR es la forma más rápida de que te agreguen como amigo en persona.' },
            { id: 'share-link', title: 'Comparte tu álbum 🔗',
              body: 'Copia tu link público para que tus amigos vean qué te falta y qué tienes repetido. Tu teléfono nunca se expone.' },
          ]}
          onClose={(completed) => {
            setShowTour(false);
            if (completed) safeStorage.set(`productTour_${user.uid}`, '1');
          }}
        />
        </Suspense>
      )}
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [friendUid, setFriendUid] = useState(null);

  useEffect(() => {
    // Check for friend link
    const params = new URLSearchParams(window.location.search);
    const f = params.get('friend');
    if (f) setFriendUid(f);

    // Sync friendUid with the URL when the user uses browser back/forward.
    // Before this listener, hitting "Back" cleared the ?friend= param in
    // the URL but state.friendUid stayed set, so the UI kept showing
    // TradeMatch for the previous friend.
    const onPopState = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newFriend = newParams.get('friend');
      setFriendUid(newFriend || null);
    };
    window.addEventListener('popstate', onPopState);

    // Listen to Firebase Auth
    const unsub = auth.onAuthStateChanged((u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => {
      unsub();
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  if (authLoading) {
    return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',background:'var(--bg-main)',color:'white'}}>Verificando sesión...</div>;
  }

  if (!user) {
    if (friendUid) {
      return (
        <Suspense fallback={<RouteFallback />}>
          <PublicProfile friendUid={friendUid} onLogin={() => setFriendUid(null)} />
        </Suspense>
      );
    }
    return <Login />;
  }

  const clearFriend = (uid = null) => {
    if (!uid) window.history.replaceState({}, '', window.location.pathname);
    setFriendUid(uid);
  };

  // Si el usuario abre SU PROPIO link (?friend={user.uid}) estando
  // logueado con esa cuenta, no tiene sentido cargar TradeMatch — eso
  // generaba un "trato contigo mismo" raro y, peor, dispara la carga
  // del chunk lazy de TradeMatch que puede fallar con
  // "Failed to fetch dynamically imported module" cuando el SW tiene
  // un index.html stale apuntando a un hash viejo.
  //
  // Solución: limpiar el friendUid de la URL + state apenas detectamos
  // el self-link. La app abre normal en la vista de Resumen.
  if (friendUid && friendUid === user.uid) {
    window.history.replaceState({}, '', window.location.pathname);
    // No usamos setFriendUid acá (estamos en render); el efecto sync
    // de popstate no captura replaceState. Lo simulamos con un
    // setTimeout para que la limpieza ocurra después del render.
    setTimeout(() => setFriendUid(null), 0);
    // Renderizamos sin friendUid en este render — equivalente a no
    // tener el param.
    return <AlbumApp user={user} friendUid={null} onClearFriend={clearFriend} />;
  }

  return <AlbumApp user={user} friendUid={friendUid} onClearFriend={clearFriend} />;
}
